import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  LayoutList,
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  Search,
  Settings,
  Users,
  Check,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const ReportTypes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportTypes, setReportTypes] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingPermissions, setEditingPermissions] = useState(null);
  const [permissions, setPermissions] = useState({});
  const itemsPerPage = 10;

  const userGroups = ['admin', 'financeiro', 'operacao', 'vendas', 'logistica'];
  const permissionTypes = [
    { key: 'can_view', label: 'Ver' },
    { key: 'can_create', label: 'Criar' },
    { key: 'can_edit', label: 'Editar' },
    { key: 'can_delete', label: 'Deletar' },
    { key: 'can_share', label: 'Compartilhar' },
  ];

  useEffect(() => {
    fetchReportTypes();
  }, []);

  const fetchReportTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/types?include_inactive=true`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setReportTypes(data.report_types || []);
      }
    } catch (err) {
      console.error('Failed to fetch types');
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async (typeId) => {
    try {
      const res = await fetch(`${API_URL}/api/report-builder/types/${typeId}/permissions`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        // Convert to a map for easier access
        const permMap = {};
        data.permissions?.forEach(p => {
          permMap[p.user_group] = p;
        });
        setPermissions(permMap);
        setEditingPermissions(typeId);
      }
    } catch (err) {
      toast.error('Erro ao carregar permissões');
    }
  };

  const savePermissions = async () => {
    if (!editingPermissions) return;

    try {
      const permList = userGroups.map(group => ({
        user_group: group,
        can_view: permissions[group]?.can_view || false,
        can_create: permissions[group]?.can_create || false,
        can_edit: permissions[group]?.can_edit || false,
        can_delete: permissions[group]?.can_delete || false,
        can_share: permissions[group]?.can_share || false,
        can_export: permissions[group]?.can_export || false,
        can_schedule: permissions[group]?.can_schedule || false,
      }));

      const res = await fetch(`${API_URL}/api/report-builder/types/${editingPermissions}/permissions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(permList)
      });

      if (res.ok) {
        toast.success('Permissões salvas!');
        setEditingPermissions(null);
      }
    } catch (err) {
      toast.error('Erro ao salvar permissões');
    }
  };

  const togglePermission = (group, permKey) => {
    setPermissions({
      ...permissions,
      [group]: {
        ...permissions[group],
        [permKey]: !permissions[group]?.[permKey]
      }
    });
  };

  const handleDelete = async (typeId) => {
    if (!window.confirm('Tem certeza que deseja excluir este tipo?')) return;

    try {
      const res = await fetch(`${API_URL}/api/report-builder/types/${typeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Tipo excluído');
        fetchReportTypes();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao excluir');
      }
    } catch (err) {
      toast.error('Erro ao excluir tipo');
    }
  };

  const filteredTypes = reportTypes.filter(type =>
    type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    type.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const paginatedTypes = filteredTypes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-types">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight font-['Space_Grotesk']">
              Gerenciar Tipos de Relatório
            </h1>
            <p className="text-muted-foreground text-sm">
              Configure os tipos disponíveis e suas permissões
            </p>
          </div>
        </div>
        <Button className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600">
          <Plus className="h-4 w-4" />
          Novo Tipo
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar tipos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Types Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Nome</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Slug</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Módulo</th>
                  <th className="text-center py-3 px-4 font-medium text-muted-foreground">Sistema</th>
                  <th className="text-center py-3 px-4 font-medium text-muted-foreground">Ativo</th>
                  <th className="text-right py-3 px-4 font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTypes.map((type) => (
                  <tr key={type.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: type.color || '#6b7280' }}
                        />
                        <span className="font-medium">{type.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline" className="font-mono text-xs">
                        {type.slug}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {type.source_module || '-'}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {type.is_system ? (
                        <Check className="h-4 w-4 text-emerald-600 mx-auto" />
                      ) : (
                        <X className="h-4 w-4 text-muted-foreground mx-auto" />
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {type.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Ativo</Badge>
                      ) : (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => fetchPermissions(type.id)}
                          title="Permissões"
                        >
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {!type.is_system && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(type.id)}
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, filteredTypes.length)} de {filteredTypes.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Permissions Modal */}
      {editingPermissions && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600" />
                Permissões por Grupo
              </CardTitle>
              <CardDescription>
                Configure as permissões para cada grupo de usuários
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium">Grupo</th>
                      {permissionTypes.map(p => (
                        <th key={p.key} className="text-center py-2 px-3 font-medium text-sm">
                          {p.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {userGroups.map(group => (
                      <tr key={group} className="border-b">
                        <td className="py-2 px-3 capitalize font-medium">{group}</td>
                        {permissionTypes.map(perm => (
                          <td key={perm.key} className="text-center py-2 px-3">
                            <button
                              onClick={() => togglePermission(group, perm.key)}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                                permissions[group]?.[perm.key]
                                  ? 'bg-blue-500 border-blue-500 text-white'
                                  : 'border-gray-300 hover:border-blue-300'
                              }`}
                            >
                              {permissions[group]?.[perm.key] && <Check className="h-4 w-4" />}
                            </button>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setEditingPermissions(null)}>
                  Cancelar
                </Button>
                <Button onClick={savePermissions} className="bg-blue-500 hover:bg-blue-600">
                  Salvar Permissões
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ReportTypes;
